// Per-type value widgets for project parameters, shared by the parameter modal and the bulk grid so the
// two can never disagree about how a boolean, a date or a number is entered and stored.

import React from 'react';
import { strings } from '/imports/strings';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { NumberInput } from '/imports/ui/NumberInput';
import { DateInput } from '/imports/ui/DateInput';
import { DateTimeInput } from '/imports/ui/DateTimeInput';
import { ProjectManagementParameterType } from '/imports/api/projectManagement';

/** Renders the widget a parameter type expects. Each widget has its own value/onChange shape. */
export const projectManagementValueInput = (
  type: ProjectManagementParameterType | undefined,
  value: any,
  onChange: (v: any) => void,
  opts: { onBlur?: () => void, autoFocus?: boolean } = {},
) => {
  switch (type) {
    case 'string': return <Input value={value ?? ''} onChange={(v) => onChange(v || undefined)} onBlur={opts.onBlur} autoFocus={opts.autoFocus} />;
    case 'integer': return <NumberInput int value={value} onChange={(v) => onChange(v)} onBlur={opts.onBlur} autoFocus={opts.autoFocus} />;
    case 'boolean': return (
      <Select
        value={value === true ? 'true' : value === false ? 'false' : ''}
        options={[{ value: '', label: '—' }, { value: 'true', label: strings.Yes() }, { value: 'false', label: strings.No() }]}
        onChange={(v) => onChange(v === 'true' ? true : v === 'false' ? false : undefined)}
      />
    );
    case 'date': return <DateInput value={value instanceof Date ? value.toISOString().slice(0, 10) : value} onChange={(v) => onChange(v || undefined)} onBlur={opts.onBlur} autoFocus={opts.autoFocus} />;
    case 'datetime': return <DateTimeInput value={value instanceof Date ? value : (value ? new Date(value) : undefined)} onChange={(v) => onChange(v)} autoFocus={opts.autoFocus} />;
    default: return <NumberInput value={value} onChange={(v) => onChange(v)} onBlur={opts.onBlur} autoFocus={opts.autoFocus} />;
  }
};

/** Coerces a widget's raw value to what gets stored for that type. */
export const projectManagementToStored = (
  type: ProjectManagementParameterType | undefined, raw: any,
): number | string | boolean | Date | undefined => {
  if (raw == null || raw === '') return undefined;
  if (type === 'string') return String(raw);
  if (type === 'boolean') return typeof raw === 'boolean' ? raw : undefined;
  if (type === 'date') return raw instanceof Date ? raw : new Date(`${raw}T00:00:00`);
  if (type === 'datetime') return raw instanceof Date ? raw : new Date(raw);
  return typeof raw === 'number' ? raw : Number(raw);  // integer / number
};

/** Re-exported so grid code has one import for editing concerns; the parser itself lives in the api
 *  module because the xlsx import (server) must read cells exactly the same way. */
export { projectManagementParseCellValue as projectManagementParseCell } from '/imports/api/projectManagement';
